import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model/selectors';
import { useSession } from '@/features/guided-financial-planning/context';
import { omitPreviouslyFinalizedPolicies } from '@/features/guided-financial-planning/utils/finalized-policies';

/**
 * Hook that returns the appropriate multipleFundingSources based on session status.
 *
 * For completed sessions, returns the snapshotted funding sources captured at finalization.
 * For active sessions, returns the live client funding sources, without the policies an earlier
 * finalization of a reopened session saved: those are the plan's own, not existing coverage.
 *
 * This ensures views of completed sessions show consistent data as it was at the time of completion.
 */
export function useFundingSources(): MultipleFundingSources | undefined {
  const client = useSelector(selectClient);
  const { session } = useSession();
  const isCompleted = session?.status === 'completed';
  const snapshot = session?.stepData?.multipleFundingSourcesSnapshot;
  const previouslyFinalizedPolicies = session?.stepData?.previouslyFinalizedPolicies;

  const liveFundingSources = useMemo(
    () =>
      omitPreviouslyFinalizedPolicies(
        client?.multipleFundingSources,
        client?.clientId,
        previouslyFinalizedPolicies,
      ),
    [client?.multipleFundingSources, client?.clientId, previouslyFinalizedPolicies],
  );

  if (isCompleted && snapshot) return snapshot;
  return liveFundingSources;
}
